import { useState, useEffect } from 'react';
import useGoogle from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Country, State } from 'country-state-city';
import AsyncSelect from 'react-select/async';
import { getStateName, getStateCode, formatStateName } from '../../helpers/JIFI/province-resolver';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogHeader,
} from '@shadcn/components/ui/dialog';
import { Input as InputShadcn } from '@shadcn/components/ui/input';
import { Button } from '@shadcn/components/ui/button';

const EditAddressModal = ({ modal, togglemodal, address, setAddress, title = "Edit Address" }) => {
  const apiKey = 'AIzaSyACNNxCG6Oc9WaFfFslSu3jv5qJApU9PpI';
  const { placePredictions, getPlacePredictions } = useGoogle({
    apiKey: apiKey,
    options: {
      fields: ['address_component', 'types', 'geocode'],
      types: ['geocode'],
    },
  });

  useEffect(() => {
    const loaded = new CustomEvent('loadedPlaced', {
      detail: placePredictions.map((placeLine) => ({
        label: placeLine.description,
        value: placeLine,
      })),
    });
    document.dispatchEvent(loaded);
  }, [placePredictions]);

  const [selected, setSelected] = useState(null);

  const getPlaceData = async (placeID) => {
    return await new Promise((res, rej) => {
      const map = new window.google.maps.Map(
        document.getElementById('test-map'),
        { zoom: 15 }
      );
      var request = {
        placeId: placeID,
        fields: ['address_components'],
      };

      const service = new window.google.maps.places.PlacesService(map);
      service.getDetails(request, callback);

      function callback(place, status) {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          res(place);
        } else {
          rej(place);
        }
      }
    });
  };

  const loadOptions = (inputValue, callback) => {
    getPlacePredictions({ input: inputValue });
    document.addEventListener('loadedPlaced', function (e) {
      callback(e.detail);
    });
  };

  const [name, setName] = useState(address.name || '');
  const [company, setCompany] = useState(address.company || '');
  const [address1, setAddress1] = useState(address.address1 || '');
  const [address2, setAddress2] = useState(address.address2 || '');
  const [city, setCity] = useState(address.city || '');
  const [zip, setZip] = useState(address.zip || '');
  const [phone, setPhone] = useState(address.phone || '');

  const [selectedState, setState] = useState(
    getStateCode(address.country, address.province)
  );
  const [selectedCountry, setCountry] = useState(
    Country.getAllCountries().find((val) => val.name === address.country)
      ?.isoCode
  );

  useEffect(() => {
    setName(address.name || '');
    setCompany(address.company || '');
    setAddress1(address.address1 || '');
    setAddress2(address.address2 || '');
    setCity(address.city || '');
    setZip(address.zip || '');
    setPhone(address.phone || '');
    setState(getStateCode(address.country, address.province));
    setCountry(
      Country.getAllCountries().find((val) => val.name === address.country)
        ?.isoCode
    );
  }, [address]);

  useEffect(() => {
    if (selected) {
      getPlaceData(selected.value.place_id).then((res) => {
        let items = res.address_components;
        let streetNumberGoogleAPI,
          streetAddressGoogleAPI,
          countryGoogleAPI,
          stateGoogleAPI,
          unitNoGoogleAPI,
          suburnGoogleAPI,
          postcodeGoogleAPI;

        items.map((item) => {
          if (item.types.includes('subpremise')) {
            unitNoGoogleAPI = item.long_name;
          }
          if (item.types.includes('street_number')) {
            streetNumberGoogleAPI = item.long_name;
          }
          if (item.types.includes('route')) {
            streetAddressGoogleAPI = item.long_name;
          }
          if (item.types.includes('postal_code')) {
            postcodeGoogleAPI = item.long_name;
          }
          if (item.types.includes('locality')) {
            suburnGoogleAPI = item.long_name;
          }
          if (item.types.includes('administrative_area_level_1')) {
            stateGoogleAPI = item.long_name;
          }
          if (item.types.includes('country')) {
            countryGoogleAPI = item.long_name;
          }
        });

        setCountry(
          Country.getAllCountries().find((val) => val.name === countryGoogleAPI)
            ?.isoCode
        );
        setState(getStateCode(countryGoogleAPI, stateGoogleAPI));
        setCity(suburnGoogleAPI === undefined ? '' : suburnGoogleAPI);
        setZip(postcodeGoogleAPI === undefined ? '' : postcodeGoogleAPI);
        setAddress2('');
        if (streetAddressGoogleAPI) {
          if (streetNumberGoogleAPI) {
            if (unitNoGoogleAPI) {
              setAddress1(
                unitNoGoogleAPI +
                  '/' +
                  streetNumberGoogleAPI +
                  ' ' +
                  streetAddressGoogleAPI
              );
            } else {
              setAddress1(streetNumberGoogleAPI + ' ' + streetAddressGoogleAPI);
            }
          } else {
            setAddress1(streetAddressGoogleAPI);
          }
        } else {
          setAddress1('');
        }
        setSelected('');
      });
    }
  }, [selected]);

  const handleSave = () => {
    const updatedAddress = {
      name: name,
      address1: address1,
      address2: address2,
      company: company,
      phone: phone,
      city: city,
      zip: zip,
      province: getStateName(selectedCountry, selectedState) ?? 'Victoria',
      country: Country.getCountryByCode(selectedCountry)?.name ?? 'Australia',
    };
    setAddress(updatedAddress);
    togglemodal();
  };

  return (
    <Dialog
      open={modal}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      tabindex="-1"
      size="lg"
      onOpenChange={togglemodal}
    >
      <DialogContent className="max-h-[80vh] z-[1500] min-w-[50vw] overflow-y-auto">
        <div>
          <div style={{ display: 'none' }}>
            <div id="test-map" />
          </div>
          <div>
            <DialogHeader>
              <DialogTitle toggle={togglemodal}>
                {title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Name
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setName(e.target.value)}
                    className="form-control"
                    type="text"
                    value={name}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Phone
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setPhone(e.target.value)}
                    className="form-control"
                    type="text"
                    value={phone}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Company
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setCompany(e.target.value)}
                    className="form-control"
                    type="text"
                    value={company}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Search Address
                </label>
                <div className="col-md-10">
                  <AsyncSelect
                    onClick={setSelected}
                    onChange={setSelected}
                    cacheOptions
                    loadOptions={loadOptions}
                    defaultOptions
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Country
                </label>
                <div className="col-md-10">
                  <select
                    value={selectedCountry}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      const state = State.getStatesOfCountry(e.target.value);
                      if (state.length > 0) {
                        setState(state[0].isoCode);
                      }
                    }}
                    className="form-control"
                  >
                    {Country.getAllCountries().map((country) => (
                      <option
                        key={country.isoCode}
                        value={country.isoCode}
                      >
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  State
                </label>
                <div className="col-md-10">
                  <select
                    onChange={(e) => setState(e.target.value)}
                    value={selectedState}
                    className="form-control"
                  >
                    {State.getStatesOfCountry(selectedCountry).map(
                      (state) => (
                        <option
                          key={state.isoCode}
                          value={state.isoCode}
                        >
                          {formatStateName(state.name, selectedCountry)}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Address
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setAddress1(e.target.value)}
                    className="form-control"
                    type="text"
                    value={address1}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Suburb
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setCity(e.target.value)}
                    className="form-control"
                    type="text"
                    value={city}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Postcode
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setZip(e.target.value)}
                    className="form-control"
                    type="text"
                    value={zip}
                  />
                </div>
              </div>
              <div className="form-group row">
                <label
                  htmlFor="example-text-input"
                  className="col-md-2 col-form-label"
                >
                  Apartment, suite, etc. (optional)
                </label>
                <div className="col-md-10">
                  <InputShadcn
                    onChange={(e) => setAddress2(e.target.value)}
                    className="form-control"
                    type="text"
                    value={address2}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="success" onClick={handleSave}>
                Save
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditAddressModal;
